import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
    FaAlignLeft, FaArrowLeft, FaCalculator, FaCalendarAlt, FaChartLine, FaCode,
    FaExternalLinkAlt, FaFileCode, FaKey, FaLanguage, FaMoneyBillWave, FaQrcode,
    FaRulerCombined, FaTimes, FaToolbox,
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import Calculator from '../components/tools/Calculator';
import Transliterator from '../components/tools/Transliterator';
import UnitConverter from '../components/tools/UnitConverter';
import CurrencyConverter from '../components/tools/CurrencyConverter';
import QrGenerator from '../components/tools/QrGenerator';
import PasswordGenerator from '../components/tools/PasswordGenerator';
import DateCalculator from '../components/tools/DateCalculator';
import TextTools from '../components/tools/TextTools';
import JsonFormatter from '../components/tools/JsonFormatter';
import Base64Tool from '../components/tools/Base64Tool';

// Titles and descriptions live in the locale files under `useful.tools.<id>`;
// categoryKey resolves against `useful.categories.*`. A tool either renders a
// component in the modal or, like the sheep model, loads a standalone page.
const tools = [
    {
        id: 'sheep',
        categoryKey: 'finance',
        icon: FaChartLine,
        path: '/qoy-loyihasi.html',
        colors: 'from-emerald-500 to-teal-600',
    },
    {
        id: 'calculator',
        categoryKey: 'calc',
        icon: FaCalculator,
        Component: Calculator,
        colors: 'from-blue-500 to-indigo-600',
    },
    {
        id: 'currency',
        categoryKey: 'finance',
        icon: FaMoneyBillWave,
        Component: CurrencyConverter,
        colors: 'from-green-500 to-emerald-600',
    },
    {
        id: 'unit',
        categoryKey: 'calc',
        icon: FaRulerCombined,
        Component: UnitConverter,
        colors: 'from-cyan-500 to-blue-600',
    },
    {
        id: 'date',
        categoryKey: 'calc',
        icon: FaCalendarAlt,
        Component: DateCalculator,
        colors: 'from-violet-500 to-purple-600',
    },
    {
        id: 'translit',
        categoryKey: 'text',
        icon: FaLanguage,
        Component: Transliterator,
        colors: 'from-orange-500 to-red-500',
    },
    {
        id: 'text',
        categoryKey: 'text',
        icon: FaAlignLeft,
        Component: TextTools,
        colors: 'from-amber-500 to-orange-600',
    },
    {
        id: 'json',
        categoryKey: 'text',
        icon: FaCode,
        Component: JsonFormatter,
        colors: 'from-slate-500 to-gray-700',
    },
    {
        id: 'base64',
        categoryKey: 'text',
        icon: FaFileCode,
        Component: Base64Tool,
        colors: 'from-teal-500 to-cyan-600',
    },
    {
        id: 'qr',
        categoryKey: 'generate',
        icon: FaQrcode,
        Component: QrGenerator,
        colors: 'from-fuchsia-500 to-pink-600',
    },
    {
        id: 'password',
        categoryKey: 'generate',
        icon: FaKey,
        Component: PasswordGenerator,
        colors: 'from-rose-500 to-red-600',
    },
];

const ALL_CATEGORIES = 'all';
const categoryKeys = [ALL_CATEGORIES, ...new Set(tools.map((tool) => tool.categoryKey))];

const UsefulPage = () => {
    const { isDarkMode } = useTheme();
    const { t } = useTranslation();
    const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORIES);
    const [activeTool, setActiveTool] = useState(null);

    // Escape closes the tool, which matters more here than on a preview page:
    // the modal holds real input the user may want out of quickly.
    useEffect(() => {
        if (!activeTool) return undefined;
        const onKeyDown = (event) => { if (event.key === 'Escape') setActiveTool(null); };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [activeTool]);

    const filteredTools = selectedCategory === ALL_CATEGORIES
        ? tools
        : tools.filter((tool) => tool.categoryKey === selectedCategory);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.08
            }
        }
    };

    const cardVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.5,
                ease: [0.6, -0.05, 0.01, 0.99]
            }
        }
    };

    const ActiveComponent = activeTool?.Component;

    return (
        <div className="min-h-screen py-8">
            <div className="section-container">
                {/* Back Button */}
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="mb-8"
                >
                    <Link
                        to="/"
                        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${isDarkMode
                            ? 'bg-dark/60 text-primary hover:bg-dark border border-primary/20'
                            : 'bg-white text-green-600 hover:bg-gray-50 border border-gray-200'
                            } transition-all duration-300 shadow-md`}
                    >
                        <FaArrowLeft className="rtl:rotate-180" /> {t('useful.back')}
                    </Link>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className={`${isDarkMode ? 'bg-dark-overlay' : 'bg-white'} backdrop-blur-md rounded-2xl p-8 md:p-12 shadow-xl`}
                >
                    {/* Header */}
                    <div className="text-center mb-12">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${isDarkMode
                                ? 'bg-gradient-to-r from-primary to-secondary'
                                : 'bg-gradient-to-r from-green-400 to-emerald-500'
                                }`}
                        >
                            <FaToolbox className="text-4xl text-white" />
                        </motion.div>
                        <h1 className={`text-4xl md:text-5xl font-bold mb-4 ${isDarkMode ? 'text-primary' : 'text-green-600'
                            }`}>
                            {t('useful.title')}
                        </h1>
                        <p className={`text-lg md:text-xl ${isDarkMode ? 'text-light/70' : 'text-gray-600'} max-w-2xl mx-auto`}>
                            {t('useful.subtitle')}
                        </p>
                    </div>

                    {/* Category Filter */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="flex flex-wrap justify-center gap-3 mb-10"
                    >
                        {categoryKeys.map((category) => (
                            <motion.button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                className={`px-5 py-2.5 rounded-full font-medium text-sm transition-all duration-300 ${selectedCategory === category
                                    ? isDarkMode
                                        ? 'bg-primary text-dark shadow-lg shadow-primary/30'
                                        : 'bg-green-600 text-white shadow-lg shadow-green-600/30'
                                    : isDarkMode
                                        ? 'bg-dark/50 text-light/70 hover:bg-dark hover:text-primary border border-primary/20'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                                    }`}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                {category === ALL_CATEGORIES
                                    ? t('useful.all')
                                    : t(`useful.categories.${category}`)}
                            </motion.button>
                        ))}
                    </motion.div>

                    {/* Tools Grid */}
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="visible"
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    >
                        {/* No mode="wait" here: the whole grid animates at once. */}
                        <AnimatePresence>
                            {filteredTools.map((tool) => {
                                const IconComponent = tool.icon;

                                return (
                                    <motion.button
                                        key={tool.id}
                                        variants={cardVariants}
                                        layout
                                        type="button"
                                        className={`group relative overflow-hidden rounded-xl text-left ${isDarkMode
                                            ? 'bg-dark/60 hover:bg-dark/80 border border-primary/10 hover:border-primary/30'
                                            : 'bg-white hover:bg-gray-50 border border-gray-100 hover:border-gray-200'
                                            } transition-all duration-300 shadow-lg hover:shadow-xl`}
                                        onClick={() => setActiveTool(tool)}
                                        whileHover={{ y: -5 }}
                                    >
                                        {/* Gradient Top Bar */}
                                        <div className={`h-2 w-full bg-gradient-to-r ${tool.colors}`} />

                                        <div className="p-6">
                                            <div className="flex items-start justify-between mb-4">
                                                <div className={`p-3 rounded-xl bg-gradient-to-r ${tool.colors} shadow-lg`}>
                                                    <IconComponent className="text-2xl text-white" />
                                                </div>
                                                <span className={`text-xs font-medium px-3 py-1 rounded-full ${isDarkMode
                                                    ? 'bg-primary/10 text-primary'
                                                    : 'bg-green-100 text-green-700'
                                                    }`}>
                                                    {t(`useful.categories.${tool.categoryKey}`)}
                                                </span>
                                            </div>

                                            <h3 className={`text-lg font-bold mb-2 ${isDarkMode ? 'text-light' : 'text-gray-800'
                                                }`}>
                                                {t(`useful.tools.${tool.id}.title`)}
                                            </h3>
                                            <p className={`text-sm mb-4 ${isDarkMode ? 'text-light/60' : 'text-gray-500'
                                                }`}>
                                                {t(`useful.tools.${tool.id}.description`)}
                                            </p>

                                            <div className="flex items-center justify-between pt-4 border-t border-dashed border-opacity-20 border-gray-400">
                                                <span className={`text-xs ${isDarkMode ? 'text-light/40' : 'text-gray-400'}`}>
                                                    {tool.path ? '🔗' : '⚡'} {tool.path ? t('useful.page') : t('useful.instant')}
                                                </span>
                                                <motion.span
                                                    className={`text-sm font-medium ${isDarkMode ? 'text-primary' : 'text-green-600'
                                                        } flex items-center gap-1`}
                                                    whileHover={{ x: 5 }}
                                                >
                                                    {t('useful.open')} <FaExternalLinkAlt className="text-xs" />
                                                </motion.span>
                                            </div>
                                        </div>
                                    </motion.button>
                                );
                            })}
                        </AnimatePresence>
                    </motion.div>
                </motion.div>
            </div>

            {/* Tool Modal */}
            <AnimatePresence>
                {activeTool && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
                        onClick={() => setActiveTool(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            className={`relative w-full max-w-5xl h-[85vh] flex flex-col rounded-2xl overflow-hidden ${isDarkMode ? 'bg-dark' : 'bg-white'
                                } shadow-2xl`}
                            onClick={(event) => event.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className={`flex items-center justify-between gap-3 p-4 border-b ${isDarkMode ? 'border-primary/20 bg-dark-overlay' : 'border-gray-200 bg-gray-50'
                                }`}>
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className={`p-2 rounded-lg bg-gradient-to-r ${activeTool.colors}`}>
                                        {React.createElement(activeTool.icon, { className: 'text-lg text-white' })}
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className={`font-bold truncate ${isDarkMode ? 'text-light' : 'text-gray-800'}`}>
                                            {t(`useful.tools.${activeTool.id}.title`)}
                                        </h3>
                                        <p className={`text-sm truncate ${isDarkMode ? 'text-light/60' : 'text-gray-500'}`}>
                                            {t(`useful.categories.${activeTool.categoryKey}`)}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 flex-none">
                                    {activeTool.path && (
                                        <motion.a
                                            href={activeTool.path}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={`px-4 py-2 rounded-lg flex items-center gap-2 ${isDarkMode
                                                ? 'bg-primary text-dark hover:bg-secondary'
                                                : 'bg-green-600 text-white hover:bg-green-700'
                                                } transition-colors font-medium text-sm`}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            <FaExternalLinkAlt /> {t('useful.openNewWindow')}
                                        </motion.a>
                                    )}
                                    <motion.button
                                        onClick={() => setActiveTool(null)}
                                        aria-label={t('useful.close')}
                                        className={`p-2 rounded-lg ${isDarkMode
                                            ? 'text-light/60 hover:text-light hover:bg-dark'
                                            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                            } transition-colors`}
                                        whileHover={{ scale: 1.1 }}
                                        whileTap={{ scale: 0.9 }}
                                    >
                                        <FaTimes className="text-xl" />
                                    </motion.button>
                                </div>
                            </div>

                            {/* Modal Body */}
                            {activeTool.path ? (
                                <iframe
                                    src={activeTool.path}
                                    className="w-full flex-1 border-0"
                                    title={t(`useful.tools.${activeTool.id}.title`)}
                                    sandbox="allow-same-origin allow-scripts allow-popups"
                                />
                            ) : (
                                <div className="flex-1 overflow-y-auto p-4 md:p-6">
                                    <ActiveComponent />
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default UsefulPage;
